import { useSearchParams } from "react-router-dom"

import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useParallelQueries } from "@/hooks/useHttp"
import { useRuntimeCoverage } from "@/hooks/useRuntimeCoverage"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { getCards } from "./helpers"

import type { GetCardProps } from "."

export const useFunnelTriage = (props?: GetCardProps) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const kortexInstalled = useFeatureFlags("kortexInstalled")
  const { auth } = useServices()

  let cards = getCards(t, searchParams, {
    runtime: kortexInstalled,
    isDemo: auth.isDemo(),
    ...props
  })
  let isLowRuntimeCoverage = false

  const runtimeIssuesCoverage = kortexInstalled ? cards.slice(1, 2) : []
  const contextDataRuntimeIssuesCoverage = useParallelQueries<{ data: number }>(
    runtimeIssuesCoverage.map(({ params }) => ({
      url: RESOURCES_NAMES.TRIAGE.ISSUES_COUNT,
      params,
      enabled: kortexInstalled
    }))
  )
  const contextRuntimeCoverage = useRuntimeCoverage()

  const isDataStateReady =
    contextRuntimeCoverage.isFetched &&
    !contextDataRuntimeIssuesCoverage[1]?.isLoading &&
    !contextDataRuntimeIssuesCoverage[1]?.isPending

  if (kortexInstalled && isDataStateReady) {
    const runtimeCount =
      contextDataRuntimeIssuesCoverage[0]?.[0]?.data?.data || 0

    if (contextRuntimeCoverage.isLowRuntimeCoverage) {
      isLowRuntimeCoverage = true
      cards = getCards(t, searchParams, {
        isLowRuntimeCoverage,
        runtimeCount,
        runtime: false,
        isDemo: auth.isDemo(),
        ...props
      })
    }
  }

  const getEnabled = (enabled: boolean) => {
    return enabled && (!kortexInstalled || isDataStateReady)
  }

  const context = useParallelQueries<{ data: number }>(
    cards.map(({ params, enabled }) => ({
      url: RESOURCES_NAMES.TRIAGE.ISSUES_COUNT,
      params,
      enabled: getEnabled(enabled)
    }))
  )

  const contextFixable = useParallelQueries<{ data: number }>(
    cards.map(({ params, enabled }) => ({
      url: RESOURCES_NAMES.TRIAGE.ISSUES_COUNT,
      params: { fixable: "YES", ...params },
      enabled: getEnabled(enabled)
    }))
  )

  const { isLoading: isLoadingIssues, isPending: isPendingIssues } =
    context?.[1] || {}
  const {
    isLoading: isLoadingFixableIssues,
    isPending: isPendingFixableIssues
  } = contextFixable?.[1] || {}

  const isLoading =
    isLoadingIssues ||
    isPendingIssues ||
    isLoadingFixableIssues ||
    isPendingFixableIssues

  return [
    context,
    cards,
    kortexInstalled,
    contextFixable,
    { isLoading, isLowRuntimeCoverage }
  ] as const
}
